/**
 * Right pane — every view the selected conversation produced, newest first, each pinnable to a
 * dashboard. Views appear here live as `view` SSE events arrive, before the conversation's own
 * query has refetched. Each row's face is title + caption + chart (`ViewCard`, shared with the
 * pin-board card); a template's subtitle/lens note, callouts and the basis line live behind its
 * click-to-open popup rather than inline — reference material, not the claim.
 */

import { ViewCard } from "@/features/views/view-card";
import type { DashboardSummary, ViewRecord } from "@/lib/api";

import { PinMenu } from "./pin-menu";

interface ViewsRailProps {
  views: ViewRecord[];
  dashboards: DashboardSummary[];
  onPin: (view: ViewRecord, dashboardId: string) => void;
  onUnpin: (view: ViewRecord, dashboardId: string) => void;
  onCreateDashboardAndPin: (view: ViewRecord) => void;
  pinPending: boolean;
}

export function ViewsRail({
  views,
  dashboards,
  onPin,
  onUnpin,
  onCreateDashboardAndPin,
  pinPending,
}: ViewsRailProps) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-border shrink-0 border-b p-3">
        <span className="font-heading text-foreground text-sm font-semibold">Views</span>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {views.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            Views the agent presents as evidence for its answers will appear here — pin any of them
            to the dashboard.
          </p>
        ) : (
          views.map((view) => (
            <div key={view.id} className="border-border bg-card rounded-lg border p-3">
              <div className="flex items-start justify-between gap-2">
                <ViewCard spec={view.spec} id={view.id} className="min-w-0 flex-1" />
                <PinMenu
                  view={view}
                  dashboards={dashboards}
                  pending={pinPending}
                  onPin={(dashboardId) => onPin(view, dashboardId)}
                  onUnpin={(dashboardId) => onUnpin(view, dashboardId)}
                  onCreateAndPin={() => onCreateDashboardAndPin(view)}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
